import React, { useState, useEffect } from 'react';
import { FaExchangeAlt, FaExclamationTriangle, FaArrowRight, FaSpinner, FaBuilding, FaBriefcase } from 'react-icons/fa';
import { Modal } from './common';
import VacantePicker from './VacantePicker';
import axiosInstance from '../utils/axiosConfig';
import { iniciales, rutaPuesto } from './vacantesShared';
import './FormModal.css';
import './VacantesUI.css';

/**
 * Ascenso o traslado: la persona pasa a ocupar una vacante vigente y hay que
 * decidir qué pasa con el puesto que deja, igual que en un egreso.
 * `empleado` es la fila de la tabla de empleados.
 */
function MovimientoInternoModal({ empleado, onClose, onDone }) {
  const primerNombre = (empleado.nombre || '').split(' ')[0];
  const [paso, setPaso] = useState(1);
  const [vacante, setVacante] = useState(null);
  const [destinoPuesto, setDestinoPuesto] = useState('');
  const [sedes, setSedes] = useState([]);
  const [sedeId, setSedeId] = useState('');
  const [detalles, setDetalles] = useState('');
  const [abriendoVacante, setAbriendoVacante] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axiosInstance.get('/api/sedes').then((r) => {
      setSedes(r.data);
      const actual = r.data.find((s) => s.nombre === empleado.sede);
      if (actual) setSedeId(String(actual.id));
    }).catch(() => {});
  }, [empleado.sede]);

  const elegir = (v) => {
    setVacante(v);
    setError(null);
    setPaso(2);
  };

  const confirmar = async () => {
    if (empleado.cargo && !destinoPuesto) {
      setError('Indica qué pasa con el puesto que deja');
      return;
    }
    setGuardando(true);
    setError(null);
    try {
      const r = await axiosInstance.post(`/api/empleados/${empleado.id}/movimiento`, {
        vacante_id: vacante.id,
        destino_puesto: destinoPuesto || null,
        sede_id: sedeId || null,
        detalles_cargo: detalles,
      });
      onDone(r.data.message);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo registrar el movimiento');
      setGuardando(false);
    }
  };

  const rutaActual = [empleado.gerencia, empleado.departamento, empleado.area].filter(Boolean).join(' › ');

  return (
    <Modal isOpen={true} onClose={onClose} size="xl" closeOnEscape={!abriendoVacante}>
      <Modal.Header onClose={onClose}>
        <span className="flex items-center gap-2"><FaExchangeAlt /> Movimiento interno de {empleado.nombre}</span>
      </Modal.Header>
      <Modal.Body>
        <div className="vac-pasos" aria-hidden="true">
          <span className={`vac-paso ${paso === 1 ? 'vac-paso--activo' : 'vac-paso--hecho'}`}>
            <span className="vac-paso-num">1</span> Puesto nuevo
          </span>
          <span className="vac-pasos-linea" />
          <span className={`vac-paso ${paso === 2 ? 'vac-paso--activo' : ''}`}>
            <span className="vac-paso-num">2</span> Confirmar
          </span>
        </div>

        {error && (
          <div className="form-modal-error mb-4">
            <FaExclamationTriangle />
            {error}
          </div>
        )}

        {paso === 1 ? (
          <VacantePicker
            intro={`¿A qué puesto pasa ${primerNombre}? Un ascenso o traslado cubre una vacante abierta, igual que un ingreso.`}
            onElegir={elegir}
            onAbriendoVacante={setAbriendoVacante}
            onError={setError}
          />
        ) : (
          <>
            <div className="vac-movimiento">
              <div className="vac-movimiento-lado">
                <div className="vac-ficha-puesto-etiqueta text-gray-500">Deja</div>
                <div className="flex items-start gap-3 mt-1">
                  <span className="vac-asiento vac-asiento--cubierta">{iniciales(empleado.nombre)}</span>
                  <div className="min-w-0">
                    <div className="vac-puesto-titulo">{empleado.cargo || 'Sin cargo'}</div>
                    <div className="vac-puesto-ruta">{rutaActual}</div>
                  </div>
                </div>
              </div>
              <FaArrowRight className="vac-movimiento-flecha" aria-hidden="true" />
              <div className="vac-movimiento-lado vac-movimiento-lado--nuevo">
                <div className="vac-ficha-puesto-etiqueta">Pasa a</div>
                <div className="flex items-start gap-3 mt-1">
                  <span className="vac-asiento" />
                  <div className="min-w-0">
                    <div className="vac-puesto-titulo">{vacante.titulo}</div>
                    <div className="vac-puesto-ruta">{rutaPuesto(vacante)}</div>
                    <div className="vac-puesto-ruta mt-0.5">
                      {vacante.supervisor_nombre ? `Reporta a ${vacante.supervisor_nombre}` : 'Sin supervisor'}
                    </div>
                  </div>
                </div>
                <button type="button" className="vac-link-btn mt-2" onClick={() => setPaso(1)}>Cambiar</button>
              </div>
            </div>

            {empleado.cargo && (
              <div className="form-modal-section">
                <h3><FaBriefcase /> ¿Qué pasa con el puesto de {empleado.cargo}?</h3>
                <div className="vac-decision" role="radiogroup" aria-label="Destino del puesto que deja">
                  {[
                    {
                      valor: 'reemplazar',
                      titulo: 'Se reemplaza',
                      detalle: 'Se abre la vacante del puesto que deja. Quienes le reportaban pasan a reportarle a esa vacante.',
                    },
                    {
                      valor: 'eliminar',
                      titulo: 'Se elimina el puesto',
                      detalle: 'No se busca a nadie. Quienes le reportaban pasan a su supervisor actual.',
                    },
                  ].map((op) => (
                    <label
                      key={op.valor}
                      className={`vac-decision-opcion${destinoPuesto === op.valor ? ' vac-decision-opcion--activa' : ''}`}
                    >
                      <input
                        type="radio"
                        name="destino_puesto"
                        checked={destinoPuesto === op.valor}
                        onChange={() => { setDestinoPuesto(op.valor); setError(null); }}
                      />
                      <span>
                        <span className="vac-decision-titulo block">{op.titulo}</span>
                        <span className="vac-decision-detalle block">{op.detalle}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="form-modal-section">
              <h3><FaBuilding /> Detalles</h3>
              <div className="form-modal-grid">
                <div className="form-modal-group">
                  <label htmlFor="mov-sede"><FaBuilding className="input-icon" /> Sede</label>
                  <select id="mov-sede" value={sedeId} onChange={(e) => setSedeId(e.target.value)}>
                    <option value="">Se queda en la que está</option>
                    {sedes.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                  </select>
                </div>
                <div className="form-modal-group">
                  <label htmlFor="mov-detalles"><FaBriefcase className="input-icon" /> Detalles del cargo</label>
                  <input
                    id="mov-detalles"
                    type="text"
                    value={detalles}
                    onChange={(e) => setDetalles(e.target.value)}
                    placeholder="Opcional, por ejemplo: Centro Occidente"
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
        {paso === 2 && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={confirmar}
            disabled={guardando || (!!empleado.cargo && !destinoPuesto)}
          >
            {guardando ? <FaSpinner className="animate-spin" /> : 'Confirmar movimiento'}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default MovimientoInternoModal;
